import { Divider } from "@mui/material";
import {
	CompanyTestResultFieldType,
	ExpectationFieldType,
	Severity,
} from "node-library/dist/constants";
import {
	FrontendClient,
	FrontendCompanyResultFieldHierarchyNodeMappingPackage,
	FrontendCompanyTestCategory,
	FrontendCompanyTestResultField,
	FrontendCompanyTestResultUnits,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrganization,
} from "node-library/dist/portal";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton, TextButton } from "../../../../common-components/button";
import CountryProvinceSelector from "../../../../common-components/countryAndProvinceSelector";
import {
	CommonSwitch,
	CustomCheckbox,
	CustomDropdown,
	CustomTextInput,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { validateEmail } from "../../../../helpers/inputHelper";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import { DeleteIcon } from "../../../../common-components/svgIcons";
import AddIcon from "@mui/icons-material/Add";
import { createUUID } from "node-library/dist/helpers/request_helper";
import CommonAutoCompleteDropdown from "../../../../common-components/commonAutoCompleteDropdown";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	addOrEditResult: (
		result: FrontendCompanyTestResultField,
		isEdit: boolean,
	) => void;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	loading: boolean;
	resultField?: FrontendCompanyTestResultField;
}

type ExpectationSelection = ExpectationFieldType | "none";

export default function AddOrEditResultFieldModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { company, open, setOpen, addOrEditResult, resultField, loading } =
		props;

	const [fieldName, setFieldName] = useState<string>(
		resultField?.fieldName || "",
	);
	const [fieldType, setFieldType] = useState<CompanyTestResultFieldType>(
		resultField?.fieldType || CompanyTestResultFieldType.TEXT,
	);
	const [expectationType, setExpectationType] = useState<ExpectationSelection>(
		resultField?.expectationField
			? resultField.expectationField.expectationFieldType
			: "none",
	);

	const [nodeMappingPackageIds, setNodeMappingPackageIds] = useState<
		Array<string>
	>(resultField?.nodeMappingPackageIds || []);

	const companyNodeMappingPackages: Array<FrontendCompanyResultFieldHierarchyNodeMappingPackage> =
		company.hierarchyNodeMappingPackages.sort((a, b) => {
			return a.testCategoryId.localeCompare(b.testCategoryId);
		});

	// const selectedNodeMappingPackages: Array<FrontendCompanyResultFieldHierarchyNodeMappingPackage> =
	// 	useMemo(() => {
	// 		const filtered = companyNodeMappingPackages.filter((pack) =>
	// 			nodeMappingPackageIds.includes(pack.packageId),
	// 		);

	// 		// Sort by Category Name (or ID) for a cleaner UI
	// 		return filtered.sort((a, b) => {
	// 			return a.testCategoryId.localeCompare(b.testCategoryId);
	// 		});
	// 	}, [
	// 		nodeMappingPackageIds,
	// 		companyNodeMappingPackages,
	// 		company.companyTestCategories,
	// 	]);

	const [availableUnits, setAvailableUnits] = useState<
		Array<FrontendCompanyTestResultUnits>
	>(resultField?.availableUnits || []);

	const unitIds: Array<string> = availableUnits.map((unit) => {
		return unit.unitId;
	});

	//validations
	const [fieldNameErrorMsg, setFieldNameErrorMsg] = useState<string>("");

	function handleSubmit() {
		if (!fieldName || fieldName.trim().length == 0) {
			setFieldNameErrorMsg(translation("MissingInformation"));
			return;
		}
		setFieldNameErrorMsg("");
		let resultToSubmit: FrontendCompanyTestResultField;
		if (resultField) {
			resultToSubmit = {
				...resultField,
				fieldName,
				fieldType,
				nodeMappingPackageIds,
				availableUnits,
				expectationField:
					expectationType == "none"
						? undefined
						: resultField.expectationField
							? {
									...resultField.expectationField,
									expectationFieldType: expectationType,
								}
							: {
									expectationFieldId: "",
									expectationFieldType: expectationType,
									mandatory: false,
								},
			};
		} else {
			const existingFields = company.testResultAndExpectationFields || [];
			const maxDisplayOrder =
				existingFields.length > 0
					? Math.max(...existingFields.map((f) => f.displayOrder))
					: -1;
			resultToSubmit = {
				fieldId: "",
				fieldName,
				fieldType,
				displayOrder: maxDisplayOrder + 1,
				nodeMappingPackageIds,
				availableUnits,
				expectationField:
					expectationType == "none"
						? undefined
						: {
								expectationFieldId: "",
								expectationFieldType: expectationType,
								mandatory: false,
							},
			};
		}
		addOrEditResult(resultToSubmit, resultField ? true : false);
	}

	useEffect(() => {
		if (open) {
			setFieldNameErrorMsg("");

			setFieldName(resultField?.fieldName || "");
			setFieldType(resultField?.fieldType || CompanyTestResultFieldType.TEXT);
			setExpectationType(
				resultField?.expectationField
					? resultField.expectationField.expectationFieldType
					: "none",
			);

			setNodeMappingPackageIds(resultField?.nodeMappingPackageIds || []);
			setAvailableUnits(resultField?.availableUnits || []);
		}
	}, [open]);

	const fieldNameInput: React.ReactNode = (
		<CustomTextInput
			id="result-fieldname-input"
			key="result-fieldname-input"
			value={fieldName}
			setValue={setFieldName}
			label={translation("FieldName")}
			type="text"
			required
			errorMsg={fieldNameErrorMsg}
			setErrorMsg={setFieldNameErrorMsg}
		></CustomTextInput>
	);

	const fieldTypeInput: React.ReactNode = (
		<CustomDropdown
			id={"field-type-name"}
			label={translation("FieldType")}
			options={Object.values(CompanyTestResultFieldType).map((value) => {
				return { value: value, title: translation(value) };
			})}
			value={fieldType}
			setValue={setFieldType}
		></CustomDropdown>
	);

	const expectationTypeDropdown: React.ReactNode = (
		<CustomDropdown
			id={"result-expectation-type"}
			label={translation("ExpectationField")}
			options={[
				// Add the "None" option manually
				{ value: "none", title: translation("None") },
				// Map the actual Enum values for the rest
				...Object.values(ExpectationFieldType).map((value) => ({
					value: value,
					title: translation(value),
				})),
			]}
			value={expectationType}
			setValue={setExpectationType}
		></CustomDropdown>
	);

	const removePackage = (index: number) => {
		const updatedIds = nodeMappingPackageIds.filter((_, i) => i !== index);
		setNodeMappingPackageIds(updatedIds);
	};

	const removeUnit = (index: number) => {
		const updatedUnits = availableUnits.filter((_, i) => i !== index);
		setAvailableUnits(updatedUnits);
	};
	const newTestPackage = () => {
		// Find the first package that hasn't been used yet
		const nextAvailable = companyNodeMappingPackages.find(
			(p) => !nodeMappingPackageIds.includes(p.packageId),
		);

		if (nextAvailable) {
			setNodeMappingPackageIds([
				...nodeMappingPackageIds,
				nextAvailable.packageId,
			]);
		} else {
			// Use your snackbar to warn the user if all packages are already assigned
			openSnackbar(translation("AllAvailablePackagesAssigned"), Severity.WARN);
		}
	};

	const newUnits = () => {
		// Find the first package that hasn't been used yet
		const nextAvailable = company.testResultUnits.find(
			(u) => !unitIds.includes(u.unitId),
		);

		if (nextAvailable) {
			setAvailableUnits([...availableUnits, nextAvailable]);
		} else {
			// Use your snackbar to warn the user if all packages are already assigned
			openSnackbar(translation("AllAvailableUnitsAssigned"), Severity.WARN);
		}
	};

	const nodePackageItems: Array<React.ReactNode> = nodeMappingPackageIds.map(
		(selectedId, index) => {
			// 1. Find the full object for the currently selected ID
			const selectedPackage = companyNodeMappingPackages.find(
				(p) => p.packageId === selectedId,
			);

			// 2. Filter available options so a package can't be selected twice,
			// but include the one currently selected for this specific row.
			const availableOptions = companyNodeMappingPackages.filter(
				(p) =>
					!nodeMappingPackageIds.includes(p.packageId) ||
					p.packageId === selectedId,
			);

			return (
				<div
					key={`node-pkg-${index}`}
					style={{
						display: "flex",
						gap: "10px",
						alignItems: "center",
						marginBottom: "10px",
					}}
				>
					<div style={{ width: "100%" }}>
						<CommonAutoCompleteDropdown
							id={`node-package-autocomplete-${index}`}
							label={translation("NodePackage")}
							options={availableOptions}
							// Group by looking up the category name via the testCategoryId
							groupBy={(
								pkg: FrontendCompanyResultFieldHierarchyNodeMappingPackage,
							) => {
								const category = company.companyTestCategories.find(
									(c: FrontendCompanyTestCategory) =>
										c.testCategoryId === pkg.testCategoryId,
								);
								return category
									? category.testCategoryName
									: translation("Other");
							}}
							// Display the package name in the input
							getOptionLabel={(
								pkg: FrontendCompanyResultFieldHierarchyNodeMappingPackage,
							) => pkg.packageName}
							value={selectedPackage}
							setValue={(
								newValue: FrontendCompanyResultFieldHierarchyNodeMappingPackage | null,
							) => {
								if (newValue) {
									const updatedIds = [...nodeMappingPackageIds];
									updatedIds[index] = newValue.packageId;
									setNodeMappingPackageIds(updatedIds);
								}
							}}
						/>
					</div>
					<div
						style={{ cursor: "pointer", marginTop: "15px" }} // Added margin to align with autocomplete label height
						onClick={() => removePackage(index)}
					>
						<DeleteIcon />
					</div>
				</div>
			);
		},
	);
	const nodePackagesBox: React.ReactNode = (
		<>
			<div
				className={`typography-body-02`}
				style={{
					display: "flex",
					flexWrap: "wrap",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div>{translation("NodePackages")}</div>
				<div className="submission-confirm-info-row-info">
					<TextButton
						id={"add-test-report-email-button"}
						onClick={() => {
							newTestPackage();
						}}
						size="xsmall"
					>
						<AddIcon style={{ fontSize: 20 }} />
						<span className="typography-body-02">{translation("Package")}</span>
					</TextButton>
				</div>
			</div>
			<div>{nodePackageItems}</div>
		</>
	);

	const unitItems: Array<React.ReactNode> = availableUnits.map(
		(unit, index) => {
			// 2. Filter available options so a package can't be selected twice,
			// but include the one currently selected for this specific row.
			const availableOptions = company.testResultUnits.filter(
				(u) => !unitIds.includes(u.unitId) || u.unitId === unit.unitId,
			);

			return (
				<div
					key={`unit-${index}`}
					style={{
						display: "flex",
						gap: "10px",
						alignItems: "center",
						marginBottom: "10px",
					}}
				>
					<div style={{ width: "100%" }}>
						<CommonAutoCompleteDropdown
							id={`unit-autocomplete-${index}`}
							label={translation("Unit")}
							options={availableOptions}
							// Display the package name in the input
							getOptionLabel={(unit: FrontendCompanyTestResultUnits) =>
								unit.unit
							}
							value={unit}
							setValue={(newValue: FrontendCompanyTestResultUnits | null) => {
								if (newValue) {
									const updatedUnits = [...availableUnits];
									updatedUnits[index] = newValue;
									setAvailableUnits(updatedUnits);
								}
							}}
						/>
					</div>
					<div
						style={{ cursor: "pointer", marginTop: "15px" }} // Added margin to align with autocomplete label height
						onClick={() => removeUnit(index)}
					>
						<DeleteIcon />
					</div>
				</div>
			);
		},
	);
	const nodeUnitsBox: React.ReactNode = (
		<>
			<div
				className={`typography-body-02`}
				style={{
					display: "flex",
					flexWrap: "wrap",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<div>{translation("Units")}</div>
				<div className="submission-confirm-info-row-info">
					<TextButton
						id={"add-test-report-email-button"}
						onClick={() => {
							newUnits();
						}}
						size="xsmall"
					>
						<AddIcon style={{ fontSize: 20 }} />
						<span className="typography-body-02">{translation("Unit")}</span>
					</TextButton>
				</div>
			</div>
			<div>{unitItems}</div>
		</>
	);
	const modalContent: React.ReactNode = (
		<div>
			{fieldNameInput}
			{fieldTypeInput}
			{expectationTypeDropdown}
			{nodePackagesBox}
			{nodeUnitsBox}
		</div>
	);

	const resultModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={
				props.resultField
					? translation("EditField")
					: translation("CreateField")
			}
			// headerDescription={translation("ConfirmApprovalDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="submit-client-change-button"
					id="submit-client-change-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : props.resultField ? (
							translation("Edit")
						) : (
							translation("Create")
						)
					}
					disabled={loading}
					onClick={handleSubmit}
				></GreenButton>,
			]}
		>
			{modalContent}
		</ModalWithButtons>
	);
	return resultModal;
}
